import { useState } from "react";

import { Formik } from "formik";

import { useCurrenciesQuery } from "@sso/apis/currency";
import { useAddOrganisationMutation, useEditOrganisationMutation } from "@sso/apis/organisations";
import { useGetOrganisationById } from "@sso/apis/organisations/hooks";
import { DialogActionButtons, DialogBody, DialogHeader } from "@sso/components/Dialogs/components";
import { FormikTextInput } from "@sso/components/Formik";
import FormikTypeahead, { AutoCompleteOption } from "@sso/components/Formik/FormikTypeahead";
import { AddressKeys, CountryKeys, DialogTitle, organisationInitialValues, OrganisationKeys } from "@sso/constant";
import { useAppSelector } from "@sso/hooks/useRedux";
import CountryTypeahead from "@sso/pages/auth/SignUp/CountryTypeahead";
import { selectDialog } from "@sso/store/slices/dialog";
import { Country, Organisation } from "@sso/types";
import { CustomForm } from "@sso/ui/CustomForm";

import { organisationSchemaValidation } from "./schema";

export const OrganisationDialog = () => {
  const { id } = useAppSelector(selectDialog);
  const orgToUpdate = useGetOrganisationById(id as number);

  const [currencySearchTerm, setCurrencySearchTerm] = useState<string>("");

  const [addOrganisation, { isLoading: addLoading }] = useAddOrganisationMutation();
  const [editOrganisation, { isLoading: editLoading }] = useEditOrganisationMutation();

  const handleSubmit = (values: Organisation) => {
    orgToUpdate ? editOrganisation(values) : addOrganisation(values);
  };

  const handleCountryChange = (
    country: Country,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  ) => {
    setFieldValue(`${OrganisationKeys.ADDRESS}.${AddressKeys.COUNTRY}`, country);
    if (!orgToUpdate) {
      setFieldValue(`${OrganisationKeys.PHONE}`, country[CountryKeys.CALLING_CODE]);
      setFieldValue(OrganisationKeys.TRANSACTION_CURRENCY_CODE, country[CountryKeys.DEFAULT_CURRENCY_CODE]);
    }
  };

  const handleCountryClear = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
    setFieldValue(`${OrganisationKeys.ADDRESS}.${AddressKeys.COUNTRY}`, null);
    setFieldValue(OrganisationKeys.TRANSACTION_CURRENCY_CODE, "");
  };

  const { data: currencies, isLoading } = useCurrenciesQuery();

  const currencyOptions =
    (currencies?.map(x => ({
      key: x.code,
      value: x.code,
      label: `${x.symbol ?? ""} ${x.name} (${x.code})`,
    })) as AutoCompleteOption[]) || [];

  const onCurrencyClear = () => {
    setCurrencySearchTerm("");
  };

  return (
    <>
      <DialogHeader title={orgToUpdate ? DialogTitle.EDIT_ORGANISATION : DialogTitle.ADD_ORGANISATION} />
      <Formik
        onSubmit={handleSubmit}
        initialValues={
          orgToUpdate
            ? ({
                ...orgToUpdate,
                [OrganisationKeys.TRANSACTION_CURRENCY_CODE]:
                  orgToUpdate?.[OrganisationKeys.TRANSACTION_CURRENCY]?.code,
              } as Organisation)
            : organisationInitialValues
        }
        validationSchema={organisationSchemaValidation}
      >
        {({ setFieldValue }) => (
          <CustomForm width="100%" overflow="hidden">
            <DialogBody>
              <FormikTextInput name={OrganisationKeys.NAME} label="Organisation Name" />
              <FormikTextInput name={OrganisationKeys.BILLING_EMAIL} label="Billing Email" />
              <FormikTextInput
                name={`${OrganisationKeys.ADDRESS}.${AddressKeys.FIRST_LINE}`}
                label="Address First Line"
              />
              <FormikTextInput
                name={`${OrganisationKeys.ADDRESS}.${AddressKeys.SECOND_LINE}`}
                label="Address Second Line"
              />
              <FormikTextInput name={`${OrganisationKeys.ADDRESS}.${AddressKeys.CITY}`} label="City" />
              <FormikTextInput name={`${OrganisationKeys.ADDRESS}.${AddressKeys.STATE}`} label="State" />
              <FormikTextInput name={`${OrganisationKeys.ADDRESS}.${AddressKeys.POSTCODE}`} label="Postcode" />
              <CountryTypeahead
                name={`${OrganisationKeys.ADDRESS}.${AddressKeys.COUNTRY_ID}`}
                clear={() => handleCountryClear(setFieldValue)}
                handleChange={(c: Country) => handleCountryChange(c, setFieldValue)}
              />
              <FormikTypeahead
                disabled={isLoading}
                items={currencyOptions.filter(x => x.label.toLowerCase().includes(currencySearchTerm.toLowerCase()))}
                label="Currency"
                onSearch={(term: string) => setCurrencySearchTerm(term)}
                clear={() => onCurrencyClear()}
                name={OrganisationKeys.TRANSACTION_CURRENCY_CODE}
              />
              <FormikTextInput name={OrganisationKeys.PHONE} label="Phone" />
              <FormikTextInput name={OrganisationKeys.VAT_NUMBER} label="VAT Number" />
            </DialogBody>
            <DialogActionButtons loading={addLoading || editLoading} buttonTitle={orgToUpdate ? "Edit" : "Add"} />
          </CustomForm>
        )}
      </Formik>
    </>
  );
};
